import { useState } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { Loader2, Lock } from "lucide-react";
import api, { apiError } from "./api";
import { useAdminAuth } from "./AdminAuth";
import { inputCls, labelCls } from "./ui";

export const AdminLogin = () => {
    const { user, setUser } = useAdminAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [form, setForm] = useState({ email: "", password: "" });
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);

    if (user) return <Navigate to={location.state?.from || "/"} replace />;

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        setError("");
        try {
            const res = await api.post("/api/auth/login", { email: form.email.trim(), password: form.password });
            setUser(res.data);
            navigate(location.state?.from || "/", { replace: true });
        } catch (err) {
            setError(apiError(err, "Λάθος email ή κωδικός."));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="flex min-h-screen items-center justify-center bg-[#052536] px-6" data-testid="admin-login-page">
            <div className="w-full max-w-sm rounded-3xl border border-white/10 bg-[#093448] p-8 shadow-2xl">
                <div className="mb-8 flex flex-col items-center text-center">
                    <img src="/brand/app-icon-256.webp" alt="TheraOffice" className="h-16 w-16 rounded-2xl" />
                    <h1 className="mt-4 text-xl font-bold text-[#F2F5F6]">TheraOffice Admin</h1>
                    <p className="mt-1 flex items-center gap-1.5 text-xs text-[#697B80]">
                        <Lock size={12} /> Πρόσβαση μόνο για διαχειριστές
                    </p>
                </div>
                <form onSubmit={submit} className="space-y-5">
                    <div>
                        <label htmlFor="admin-email" className={labelCls}>Όνομα χρήστη</label>
                        <input
                            id="admin-email"
                            type="text"
                            required
                            autoComplete="username"
                            value={form.email}
                            onChange={(e) => setForm({ ...form, email: e.target.value })}
                            className={inputCls}
                            data-testid="admin-login-email"
                        />
                    </div>
                    <div>
                        <label htmlFor="admin-password" className={labelCls}>Κωδικός</label>
                        <input
                            id="admin-password"
                            type="password"
                            required
                            autoComplete="current-password"
                            value={form.password}
                            onChange={(e) => setForm({ ...form, password: e.target.value })}
                            className={inputCls}
                            data-testid="admin-login-password"
                        />
                    </div>
                    {error && (
                        <p className="rounded-xl border border-red-400/30 bg-red-400/10 px-4 py-2.5 text-sm text-red-300" data-testid="admin-login-error">
                            {error}
                        </p>
                    )}
                    <button
                        type="submit"
                        disabled={busy}
                        className="flex w-full items-center justify-center gap-2 rounded-full bg-[#22A6C0] py-3 text-sm font-bold text-[#052536] transition-colors duration-200 hover:bg-[#4CC9DC] disabled:opacity-50"
                        data-testid="admin-login-submit"
                    >
                        {busy && <Loader2 size={16} className="animate-spin" />}
                        Σύνδεση
                    </button>
                </form>
                <a href="/" className="mt-6 block text-center text-xs text-[#697B80] transition-colors hover:text-[#4CC9DC]">
                    ← Επιστροφή στο site
                </a>
            </div>
        </div>
    );
};
